/**
 * En-tête de la vitrine du produit — cinq entrées, plus un bouton.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * L'ORDRE DU MENU, ET POURQUOI IL N'EST PAS ARBITRAIRE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *   Ce que ça fait · La campagne · Tarif · Documentation · Nous écrire
 *                                                        · [Essayer trente jours]
 *
 *  · **« Ce que ça fait » avant « La campagne »** : la globalité d'abord, le
 *    différenciateur ensuite. C'est l'ordre du récit, et un menu qui contredit
 *    le récit désoriente ;
 *  · ★ **« Documentation » en QUATRIÈME, entre le tarif et le contact.** C'est
 *    la place d'un directeur qui a vu le prix et veut savoir si le produit est
 *    fini avant d'écrire. La mettre en dernier ou en pied de page reviendrait à
 *    la gâcher : une documentation publique et complète EST un argument de
 *    vente — elle prouve un produit terminé, pas une promesse ;
 *  · **cinq entrées, pas six.** Six entrées textuelles passent à la ligne sur
 *    un écran de treize pouces, et le bouton se retrouve seul en dessous ;
 *  · **pas d'entrée « Événements »** : la bande d'aiguillage sous le hero fait
 *    le travail sans coûter une place.
 *
 * ★ **« Essayer trente jours » est traité en BOUTON, pas en lien.** C'est le
 *   seul appel à l'action du menu ; il doit se voir, et il reste visible au
 *   doigt quand les cinq liens passent dans le déroulé.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ LE LIEN DE DOCUMENTATION EST UN LIEN DE ROUTE, PAS UNE ANCRE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Les quatre autres entrées défilent dans la page ; celle-ci change d'écran.
 * `/aide` est servi depuis l'apex COMME depuis le sous-domaine d'une salle, par
 * le même artefact et sans garde de session (`router.tsx`, chantier L) — c'est
 * ce qui autorise la vitrine à y renvoyer sans que la documentation devienne un
 * second site à republier.
 *
 * **La règle qui va avec** : si `/aide` cessait d'être servi à l'apex, cette
 * entrée NE PARTIRAIT PAS. Un menu qui promet un manuel et rend un 404 fait
 * exactement le dommage que la refonte cherche à éviter. Un test tient cette
 * condition à chaque exécution.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * IL NE PORTE PAS LE NOM D'UN THÉÂTRE, ET N'OFFRE PAS SES PORTES
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La vitrine ne vit que sur l'apex du domaine, où **aucun théâtre n'est
 * desservi** (`features/accueil-theatre/racine.ts`). Elle parle donc en son nom
 * propre : celui du produit et de son éditeur. Et elle n'offre ni `/portail` ni
 * `/admin`, qui n'existent pas à cette adresse — ils sont sur la page d'accueil
 * de chaque théâtre, où leurs publics les cherchent.
 */

import { Link } from 'react-router'

import type { Traduit } from '@tb/shared'

import { SelecteurLangueVitrine } from '../../components/SelecteurLangue'
import { useTraduireVitrine } from '../../lib/langue'

import {
  ancre,
  lienAideVitrine,
  ID_CAMPAGNE,
  ID_CONTACT,
  ID_COUVERTURE,
  ID_TARIFS,
} from './ancres'
import { Conteneur } from './elements'
import { CHEMIN_ESSAI } from './publics'
import { ENTETE } from './textes'
import { estSiteRidomio } from './marche'

function LogoProduit() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" focusable="false">
      <rect
        x="0.75"
        y="0.75"
        width="24.5"
        height="24.5"
        rx="6"
        className="fill-white/10 stroke-white/25"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 5.5h15v3.2c0 4.4-3.3 6.6-7.5 9.3C8.8 15.3 5.5 13.1 5.5 8.7V5.5Z"
        className="fill-rideau-400"
      />
      <path d="M13 18v3" className="stroke-rideau-200" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

/**
 * ★ L'en-tête est passé sur le NOIR DE SALLE le 31/08, pour une raison de
 *   dessin et une seule : il surplombe désormais un hero sombre, et une barre
 *   claire posée dessus coupait la page en deux dès le premier pixel. Collant,
 *   il traverse ensuite les sections claires — d'où le fond opaque plutôt
 *   qu'un simple `backdrop-blur`, qui laissait passer le texte en dessous.
 */
const LIEN_BASE =
  'rounded-md px-2.5 py-2 text-sm font-medium whitespace-nowrap text-slate-300 no-underline ' +
  'transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-2'

/**
 * Les quatre ancres du menu. La documentation est traitée à part, plus bas :
 * c'est le seul lien de ROUTE, et le seul dont la présence est conditionnée.
 */
const ENTREES: readonly { readonly libelle: Traduit; readonly id: string }[] = [
  { libelle: ENTETE.ceQueCaFait, id: ID_COUVERTURE },
  { libelle: ENTETE.laCampagne, id: ID_CAMPAGNE },
  { libelle: ENTETE.tarif, id: ID_TARIFS },
]

export function EnteteVitrine() {
  /* ★ `false` : la vitrine vit sur l'apex et n'appartient à aucun théâtre.
       Elle ne doit donc pas interroger `GET /theatre/publique` pour sa langue
       — le visiteur et son navigateur décident seuls. */
  const t = useTraduireVitrine()

  return (
    <header className="border-b border-white/10 bg-nuit-950 sm:sticky sm:top-0 sm:z-20">
      <Conteneur className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-3">
        <a href="#contenu" className="flex items-center gap-2.5 no-underline">
          <LogoProduit />
          <span className="text-[1.0625rem] font-semibold tracking-tight text-white">
            {estSiteRidomio() ? 'RidoMio' : t(ENTETE.produit)}{' '}
            <span className="hidden font-normal text-slate-400 min-[430px]:inline">
              {t(ENTETE.parIlygo)}
            </span>
          </span>
        </a>

        <nav aria-label={t(ENTETE.navigation)} className="flex flex-wrap items-center gap-1">
          {/* Au doigt, les cinq liens s'effacent au profit du seul bouton : cinq
              ancres sur 375 px prennent trois lignes et repoussent le titre hors
              de l'écran. Ils reviennent dès `md`, où la place existe. */}
          <div className="hidden items-center gap-1 md:flex">
            {ENTREES.map((entree) => (
              <a key={entree.id} href={ancre(entree.id)} className={LIEN_BASE}>
                {t(entree.libelle)}
              </a>
            ))}
            <a href={lienAideVitrine()} className={LIEN_BASE}>
              {t(ENTETE.documentation)}
            </a>
            <a href={ancre(ID_CONTACT)} className={LIEN_BASE}>
              {t(ENTETE.nousEcrire)}
            </a>
          </div>

          {/* Au doigt, la documentation reste accessible : c'est la seule des
              cinq qui mène ailleurs, et donc la seule qu'une ancre de page ne
              rattraperait pas en défilant. */}
          <a href={lienAideVitrine()} className={`${LIEN_BASE} md:hidden`}>
            {t(ENTETE.documentation)}
          </a>

          {/* ★★ I18N-02 — LE SÉLECTEUR EST DANS L'EN-TÊTE, PAS DANS LE PIED.
              Un visiteur alémanique qui tombe sur une page française décide en
              trois secondes s'il reste. Lui demander de faire défiler
              vingt-quatre sections pour trouver comment changer de langue,
              c'est le perdre avant qu'il ait lu la première phrase. */}
          <SelecteurLangueVitrine className="ml-1 text-slate-300" />

          <Link
            to={CHEMIN_ESSAI}
            className="ml-1 inline-flex min-h-9 items-center rounded-lg bg-rideau-600 px-4 text-sm font-semibold whitespace-nowrap text-white no-underline transition-colors hover:bg-rideau-500"
          >
            {t(ENTETE.essai)}
          </Link>
        </nav>
      </Conteneur>
    </header>
  )
}
